import React, { useEffect, useState } from "react";
import imageCompression from "browser-image-compression";
import heic2any from "heic2any";
import { INSPECTION_STATUS } from "../../../../constant/constant";
import parse from "html-react-parser";
import Loader from "../../../../library/common/Loader";
import { compareLocations } from "../../../../utils/swInspectionHelper";

const NewOpenItem = ({ inQuestion, reportId, showItemId = false }) => {
  const [compressedImages, setCompressedImages] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchImageAsFile = async (url) => {
    if (!url || typeof url !== "string") {
      throw new Error("Invalid or missing image URL");
    }

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${url}`);
    }

    const blob = await response.blob();

    const extension = url.split(".").pop()?.toLowerCase() || "jpg";

    return new File([blob], `image.${extension}`, {
      type: blob.type || `image/${extension}`,
    });
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;

      reader.readAsDataURL(file);
    });
  };

  const convertHeicToJpeg = async (file) => {
    try {
      const convertedBlob = await heic2any({
        blob: file,
        toType: "image/jpeg",
        quality: 0.8,
      });

      const jpegBlob = Array.isArray(convertedBlob)
        ? convertedBlob[0]
        : convertedBlob;

      return new File(
        [jpegBlob],
        file.name.replace(/\.(heic|heif)$/i, ".jpg"),
        {
          type: "image/jpeg",
        },
      );
    } catch (error) {
      console.error("HEIC conversion failed:", error);
      throw error;
    }
  };

  useEffect(() => {
    const compressImages = async () => {
      setLoading(true);

      try {
        const imagesByLocation = {};

        if (
          inQuestion?.detailsReportView &&
          Array.isArray(inQuestion.detailsReportView)
        ) {
          await Promise.all(
            inQuestion.detailsReportView.map(
              async (itemDetail, itemDetailIndex) => {
                if (
                  itemDetail?.locationsReportView &&
                  Array.isArray(itemDetail.locationsReportView)
                ) {
                  await Promise.all(
                    [...itemDetail.locationsReportView]
                      .sort((a, b) => compareLocations(a.location, b.location))
                      .map(
                      async (item, locationIndex) => {
                        if (item?.files && Array.isArray(item.files)) {
                          const files = await Promise.all(
                            item.files.map(async (img) => {
                              try {
                                if (!img?.filePath) {
                                  return null;
                                }

                                const file = await fetchImageAsFile(
                                  img.filePath,
                                );

                                const fileName = img.filePath.toLowerCase();

                                const isHeic =
                                  fileName.endsWith(".heic") ||
                                  fileName.endsWith(".heif") ||
                                  file.type?.toLowerCase()?.includes("heic") ||
                                  file.type?.toLowerCase()?.includes("heif");

                                let fileToProcess = file;

                                if (isHeic) {
                                  fileToProcess = await convertHeicToJpeg(file);
                                }

                                const compressedFile = await imageCompression(
                                  fileToProcess,
                                  {
                                    maxSizeMB: 0.1,
                                    maxWidthOrHeight: 1920,
                                    useWebWorker: true,
                                  },
                                );

                                const base64 =
                                  await fileToBase64(compressedFile);

                                return {
                                  filePath: base64,
                                  fileId: img.fileId,
                                };
                              } catch (error) {
                                console.error(
                                  "Image processing failed:",
                                  img?.filePath,
                                  error,
                                );

                                return null;
                              }
                            }),
                          );

                          const key = `${itemDetailIndex}-${locationIndex}`;

                          imagesByLocation[key] = files.filter(Boolean);
                        }
                      },
                    ),
                  );
                }
              },
            ),
          );
        }

        setCompressedImages(imagesByLocation);
      } catch (error) {
        console.error("Image loading error:", error);
      } finally {
        setLoading(false);
      }
    };

    compressImages();
  }, [inQuestion]);

  return (
    <>
      {loading && <Loader />}

      {inQuestion?.detailsReportView?.map((itemDetail, itemDetailIndex) => (
        <React.Fragment key={itemDetailIndex}>
          {[...(itemDetail?.locationsReportView || [])]
            .sort((a, b) => compareLocations(a.location, b.location))
            .map((item, locationIndex) => {
              const key = `${itemDetailIndex}-${locationIndex}`;

              return (
                <tr className="tableBody" key={key}>
                  {showItemId && <td>{item?.locationId}</td>}
                  <td>{item?.location}</td>

                  <td>
                    <div className="itemFondText">
                      <span>
                        {itemDetail?.itemFound}
                        {itemDetail?.details && <> - </>}
                      </span>

                      {itemDetail?.details && (
                        <span>{parse(unescape(itemDetail?.details))}</span>
                      )}
                    </div>
                  </td>

                  <td>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                      }}
                    >
                      {compressedImages[key]?.map((img) => (
                        <img
                          key={img.fileId}
                          src={img.filePath}
                          alt="inspection"
                          className="imgViewReport"
                        />
                      ))}
                    </div>
                  </td>

                  <td>
                    <div className="statuss">
                      {item?.statusId === INSPECTION_STATUS?.Closed &&
                      item?.closedReportId === reportId ? (
                        <div className="closed">Closed</div>
                      ) : (
                        <div className="open">Open</div>
                      )}
                    </div>
                  </td>

                  <td>{item?.dateCreated}</td>
                </tr>
              );
            })}
        </React.Fragment>
      ))}
    </>
  );
};

export default NewOpenItem;
